import React, { SyntheticEvent, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { AnimatePresence, motion } from 'motion/react';

import { useFleet } from '../../hooks/useFleet';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { useToggle } from '../../hooks/useToggle';
import { useEventListener } from '../../hooks';
import { EventsFilter, FleetUUID } from '../../interfaces';
import { ClearLink } from '../filter/FilterControls';
import ChevronDownIcon from '../../assets/images/icons/chevron-down.svg';
import { basic, grey } from '../../theme/colors';
import { HelpIcon } from './helpIcon';
import { FleetUUIDs } from '../../enums';

interface Props {
  name: string;
  count?: any;
  children: any[] | any;
  defaultCollapsed?: boolean;
  persistenceID?: string;
  className?: string;
  triggerSize?: 'sm' | 'md' | 'lg';
  'data-test'?: any;

  clear?: () => void;
  field?: keyof EventsFilter;
  order?: keyof EventsFilter; // used to flex-order fields without the auto-clearability
  clearValue?: any;
  onClick?: () => void;
  verticalWrapper?: boolean;
  helpText?: string;
}

const CollapsibleWrapper = styled.div<{ order?: number }>`
  margin: 0 22px;
  padding: 22px 0;
  border-bottom: 1px solid ${basic.haydenBorder};
  display: flex;
  flex-direction: column;
  order: ${({ order }) => order ?? 0};
`;

const CollapsibleContentWrapper = styled.div<{ verticalWrapper?: boolean }>`
  padding-top: 16px;
  ${({ verticalWrapper }) =>
    verticalWrapper &&
    css`
      display: flex;
      align-items: center;
      justify-content: space-between;
    `}
`;

const BadgePill = styled.span`
  vertical-align: middle;
  transition: 150ms ease all;
  opacity: 1;
  position: relative;
  top: 0;
  overflow: hidden;
  border: 1px solid ${basic.haydenAccent};
  padding: 2px 6px;
  border-radius: 14px;
  color: ${grey.haydenGreyShade1};
  font-size: 12px;
  line-height: 14px;
  font-weight: 400;
  span {
    opacity: 0;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    background-color: colors.$hayden-white;
    transition: 300ms linear all;
    text-align: center;
    border-radius: 14px;
    padding-top: 1px;
  }
  &:hover {
    span {
      opacity: 1;
      background: white;
    }
  }
`;

const CollapsibleIcon = styled(motion.img)`
  margin: 0 10px;
`;

const CollapsibleTrigger = styled.div<{ isCollapsed?: boolean; triggerSize?: 'sm' | 'md' | 'lg' }>`
  cursor: pointer;
  transition: 300ms ease margin-bottom;
  color: ${grey.haydenGreyShade1};
  font-size: 16px;
  line-height: 24px;
  font-weight: 500;
  position: relative;
  top: 0;

  ${({ isCollapsed }) =>
    isCollapsed &&
    css`
      margin-bottom: 0;
    `}

  ${({ triggerSize }) =>
    triggerSize === 'sm' &&
    css`
      font-size: 24px;
      line-height: 28px;
      font-weight: 500;
    `}
       ${({ triggerSize }) =>
    triggerSize === 'md' &&
    css`
      font-size: 20px;
      line-height: 26px;
      font-weight: 600;
    `}
      ${({ triggerSize }) =>
    triggerSize === 'lg' &&
    css`
      font-size: 16px;
      line-height: 18px;
      font-weight: 600;
    `}
`;

const AbsoluteRight = styled.div`
  position: absolute;
  right: 0;
  top: 0;
`;

const orderForAssetMgmt: (keyof EventsFilter)[] = ['tags', 'dateTo', 'time_of_day', 'bus_stop_ids', 'block_names', 'vehicle_ids', 'flag'];

const filtersOrder: Record<FleetUUID, (keyof EventsFilter)[]> = {
  [FleetUUIDs.SacRTBusStopShelter]: orderForAssetMgmt,
  [FleetUUIDs.SacRTBusStopShelterActivePilot]: orderForAssetMgmt,
  [FleetUUIDs.VanTesting]: orderForAssetMgmt,
};

export function Collapsible(props: Props) {
  const defaultValue = props.defaultCollapsed ?? true;
  const fleet = useFleet();

  const [lsValue, setLSValue] = useLocalStorage(props.persistenceID, defaultValue);
  const defaultIsCollapsed = props.persistenceID ? lsValue : defaultValue;
  const [isCollapsed, toggle, setToggle] = useToggle(defaultIsCollapsed);
  const [visibleCount, setVisibleCount] = useState(props.count || '.');
  const { name, children, count } = props;

  const randomId = useRef<string>(`collapsible-${Math.random().toString(36).substring(2, 15)}`);

  // we're using the visible count, so that the animation for the count badge
  // doesn't have this weird "change to zero and disappear" behaviour upon clearing
  // filters
  useEffect(() => {
    if (props.count) {
      setVisibleCount(props.count);
    }
  }, [props.count]);

  useEffect(() => {
    if (props.persistenceID) {
      setLSValue(isCollapsed);
    }
  }, [isCollapsed, props.persistenceID]);

  useEventListener('trigger_collapse', () => {
    setToggle(true);
  });

  const clear = (e: SyntheticEvent) => {
    e.stopPropagation();
    e.preventDefault();
    return props.clear && props.clear();
  };

  const getOrder = (key?: keyof EventsFilter) => {
    const MAX_ORDER = 9999;
    if (!key) {
      return MAX_ORDER;
    }
    const index = filtersOrder[fleet.uuid]?.indexOf(key);
    return index >= 0 ? index : MAX_ORDER;
  };

  const className = `hdn-collapsible ${isCollapsed ? 'collapsed ' : ''}${props.className || ''}`;

  return (
    <CollapsibleWrapper
      className={className}
      data-test={props['data-test']}
      onClick={props.onClick}
      order={getOrder(props.order ?? props.field)}
    >
      <CollapsibleTrigger
        isCollapsed={isCollapsed}
        triggerSize={props.triggerSize}
        onClick={toggle}
        data-test="collapse-trigger"
        tabIndex={0}
        aria-label={isCollapsed ? `Show filter ${name}` : `Hide filter ${name}`}
        aria-controls={randomId.current}
        aria-expanded={!isCollapsed}
      >
        <span role="button">{name}</span>

        <CollapsibleIcon
          src={ChevronDownIcon}
          alt=""
          animate={{ rotate: isCollapsed ? 0 : -180 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
        />

        <BadgePill hidden={!count} data-test="badge">
          {visibleCount}
          {props.clear && (
            <span onClick={clear} data-test="clear" role="button" tabIndex={0} aria-label="Click to clear">
              {(count ?? '').toString().length >= 5 ? 'Clear' : '×'}
            </span>
          )}
          {props.field && (
            <ClearLink
              field={props.field}
              value={props.clearValue}
              role="button"
              data-test="clear"
              tabIndex={0}
              aria-label="Click to clear"
            >
              {(count ?? '').toString().length >= 5 ? 'Clear' : '×'}
            </ClearLink>
          )}
        </BadgePill>

        <AbsoluteRight>{props.helpText && <HelpIcon info={props.helpText} />}</AbsoluteRight>
      </CollapsibleTrigger>

      <AnimatePresence initial={false}>
        {!isCollapsed && (
          <motion.div
            id={randomId.current}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            style={{ overflow: 'hidden' }}
            tabIndex={0}
          >
            <CollapsibleContentWrapper verticalWrapper={props.verticalWrapper}>{children}</CollapsibleContentWrapper>
          </motion.div>
        )}
      </AnimatePresence>
    </CollapsibleWrapper>
  );
}
